<template>
  <drawer :show="isShown" :focus-target="focusTarget" @change-show="changeDrawerShow">
    <template #drawer-title>
      <div class="presence-drawer-header">
        <Icon :name="iconClassEnum.EXPORT" class="presence-drawer-icon hide-tablet-down mr-3" aria-hidden="true" />
        <span>{{ drawerTitle }}</span>
      </div>
    </template>
    <template #header-right>
      <div class="d-flex">
        <AulaButton
          v-if="isReadonly && isEditable"
          variant="link"
          class="drawer-edit-button mt-0"
          :aria-label="$getText('ARIA_LABEL_EDIT')"
          @click="isReadonly = false"
        >
          <Icon :name="iconClassEnum.EDIT_PEN" class="icon" />
          <span v-if="!isMobile" class="button-text">{{ $getText('BUTTON_EDIT') }}</span>
        </AulaButton>
      </div>
    </template>
    <template #drawer-content>
      <aula-spinner v-if="isLoadingVacation" />
      <div v-else-if="currentEvent" class="register-vacation-container">
        <h2>{{ $getText('PRESENCE_UPDATE_VACATION') }}</h2>
        <b-row class="mt-3 mb-4">
          <b-col>
            {{ $getText('PRESENCE_VACATION_ANNOUNCEMENT_DESCRIPTION_1') }}<br />
            {{ $getText('PRESENCE_VACATION_ANNOUNCEMENT_DESCRIPTION_2') }}
          </b-col>
        </b-row>
        <div class="separating-line">
          <template v-if="isMobile">
            <b-row v-for="(child, index) in reportedChildren" :key="index" class="mobile mt-4 mb-2">
              <b-col cols="9">
                {{ child.name }}
                <template v-if="child.isMultipleInstitution"> ({{ child.institutionName }}) </template>
              </b-col>
              <b-col cols="3">
                <el-switch
                  v-model="child.isSelected"
                  class="mobile-switch"
                  :width="48"
                  :disabled="!isCreating"
                  @input="validateChildren"
                />
              </b-col>
            </b-row>
          </template>
          <template v-for="(child, index) in reportedChildren" v-else>
            <b-form-checkbox
              :key="index"
              v-model="child.isSelected"
              :value="true"
              :unchecked-value="false"
              :disabled="!isCreating"
              class="mr-28"
              @input="validateChildren"
            >
              {{ child.name }}
              <span v-if="child.isMultipleInstitution"> ({{ child.institutionName }}) </span>
            </b-form-checkbox>
          </template>
          <b-alert variant="danger" :show="isMissingChildren">
            {{ $getText('PRESENCE_ALERT_ABSENCE_CHOOSE_CHILD') }}
          </b-alert>
        </div>
        <b-row>
          <b-col md="6" class="mt-2 mt-lg-0">
            <label>{{ $getText('PRESENCE_VACATION_FR0M_DATE') }} <span class="mandatory">*</span></label>
            <br />
            <el-date-picker
              v-model="startDate"
              class="w-100"
              :picker-options="startDatePickerOptions"
              :format="defaultDateFormat"
              :value-format="defaultDateValueFormat"
              :disabled="!isCreating && (isReadonly || !currentEvent.canEditStartDate)"
              placeholder="dd/mm/yyyy"
              type="date"
              :class="{
                'red-border': isMissingStartDate,
                elPickerActive: isStartDateClicked,
              }"
              @focus="
                datePickerModifier();
                isStartDateClicked = true;
              "
              @blur="isStartDateClicked = false"
              @input="hasDataChanged = true"
            />
            <b-alert variant="danger" :show="isMissingStartDate">
              {{ $getText('PRESENCE_ALERT_START_DATE') }}
            </b-alert>
          </b-col>
          <b-col md="6" class="mt-2 mt-lg-0">
            <label>{{ $getText('PRESENCE_VACATION_TO_DATE') }} <span class="mandatory">*</span></label>
            <br />
            <el-date-picker
              v-model="endDate"
              class="w-100"
              :format="defaultDateFormat"
              :value-format="defaultDateValueFormat"
              :picker-options="endDatePickerOptions"
              :disabled="isReadonly"
              placeholder="dd/mm/yyyy"
              type="date"
              :class="{
                'red-border': isMissingEndDate,
                elPickerActive: isEndDateClicked,
              }"
              @focus="
                datePickerModifier();
                isEndDateClicked = true;
              "
              @blur="isEndDateClicked = false"
              @input="hasDataChanged = true"
            />
            <b-alert variant="danger" :show="isMissingEndDate">
              {{ $getText('PRESENCE_ALERT_END_DATE') }}
            </b-alert>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12" class="mt-2 mt-lg-0">
            <label>{{ $getText('PRESENCE_VACATION_NOTE') }}</label>
            <b-textarea
              v-model="comment"
              max-rows="1"
              rows="3"
              maxlength="250"
              class="w-100"
              :disabled="isReadonly"
              :placeholder="$getText('PRESENCE_VACATION_NOTE_PLACEHOLDER')"
              @change="hasDataChanged = true"
            />
          </b-col>
        </b-row>
        <aula-form-footer
          :is-loading="isSavingVacation"
          :show-delete="!isCreating"
          :show-delete-mobile="!isCreating"
          :show-delete-icon="!isCreating"
          :submit-text="isReadonly ? 'BUTTON_CLOSE' : 'BUTTON_SAVE'"
          :show-cancel="!isReadonly"
          @submitClicked="isReadonly ? handleDiscard() : onSaveClicked()"
          @deleteClicked="$refs.deletionWarningModal.show()"
          @cancelClicked="handleDiscard"
        />
      </div>
      <aula-modal
        ref="deletionWarningModal"
        ok-text="YES"
        :is-loading="isDeletingVacation"
        @cancelClicked="$refs.deletionWarningModal.hide()"
        @okClicked="deleteVacation"
      >
        {{ $getText('CALENDAR_DELETE_HOLIDAY_TEXT') }}<br />
        {{ $getText('CALENDAR_DELETE_HOLIDAY_TEXT_2') }}
      </aula-modal>
      <aula-modal
        ref="discardEditingModal"
        @cancelClicked="$refs.discardEditingModal.hide()"
        @okClicked="discardEditing()"
      >
        {{ $getText('CALENDAR_CANCEL_INFO') }}<br />{{ $getText('CALENDAR_CANCEL_INFO_2') }}
      </aula-modal>
    </template>
  </drawer>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { types } from '../../store/types/types';
import { presenceModules } from '../../../shared/enums/presenceModules';
import moment from 'moment-timezone';
import { eventTypeEnum } from '../../../shared/enums/eventTypeEnum';
import Icon from '../../../shared/components/Icon.vue';
import { iconClassEnum } from '../../../shared/enums/iconClassEnum';
import AulaButton from '../../../shared/components/AulaButton.vue';
import { useVacationDatePicker } from '../../../shared/composables/useVacationDatePicker';

export default {
  components: { AulaButton, Icon },
  setup() {
    const { startDate, endDate, startDatePickerOptions, endDatePickerOptions, isEndDateValid } =
      useVacationDatePicker();

    return {
      startDate,
      endDate,
      startDatePickerOptions,
      endDatePickerOptions,
      isEndDateValid,
    };
  },
  data: function () {
    return {
      isShown: false,
      isSavingVacation: false,
      isLoadingVacation: false,
      isDeletingVacation: false,
      reportedChildren: [],
      isMissingStartDate: false,
      isMissingEndDate: false,
      isMissingChildren: false,
      isStartDateClicked: false,
      isEndDateClicked: false,
      hasDataChanged: false,
      isReadonly: false,
      isCreating: false,
      isEditable: false,
      focusTarget: '.btn-clear',
      comment: '',
    };
  },
  computed: {
    iconClassEnum() {
      return iconClassEnum;
    },
    ...mapGetters({
      isMobile: types.GET_IS_MOBILE,
      children: types.GET_CHILDREN,
      defaultDateOptions: types.GET_DEFAULT_DATE_OPTIONS,
      defaultDateFormat: types.GET_DEFAULT_DATE_FORMAT,
      defaultDateValueFormat: types.GET_DEFAULT_DATE_VALUE_FORMAT,
      getPresenceActiveModules: types.GET_PRESENCE_ACTIVE_MODULES,
      currentEvent: types.GET_CURRENT_EVENT,
      hasCustodyOrExtendedAccess: types.GET_HAS_CUSTODY_OR_EXTENDED_ACCESS,
    }),
    drawerTitle() {
      let titleFromTextKey = '';
      if (this.isCreating) {
        titleFromTextKey = 'PRESENCE_REGISTER_VACATION';
      }
      if (this.isReadonly) {
        titleFromTextKey = 'PRESENCE_REPORT_VACATION_ANNOUNCEMENT';
      }
      if (!this.isReadonly && !this.isCreating) {
        titleFromTextKey = 'PRESENCE_UPDATE_VACATION';
      }
      return this.$getText(titleFromTextKey);
    },
  },
  methods: {
    ...mapActions({
      addAbsence: types.ADD_ABSENCE,
      getEventById: types.GET_EVENT_BY_ID,
      updateSimpleEvent: types.UPDATE_SIMPLE_EVENT,
      deleteEvent: types.DELETE_EVENT,
    }),
    async show(vacation = null, isReadonly = false) {
      this.isShown = true;
      this.isReadonly = isReadonly;
      this.isCreating = !isReadonly && !vacation;
      this.isEditable = vacation && vacation.isEditable;
      this.hasDataChanged = false;

      if (this.isCreating) {
        this.resetVacationForm();
      } else if (vacation) {
        await this.getVacationDetail(vacation.vacationId);
      }
      this.getReportedChildren();
    },
    hide() {
      this.isShown = false;
      this.$emit('onClosed');
    },
    changeDrawerShow(value) {
      if (!value) {
        this.handleDiscard();
      }
    },
    resetVacationForm() {
      this.isMissingStartDate = false;
      this.isMissingEndDate = false;
      this.isMissingChildren = false;

      this.startDate = moment().format('YYYY-MM-DD');
      this.endDate = moment().format('YYYY-MM-DD');

      this.comment = '';
    },
    discardEditing() {
      this.$refs.discardEditingModal.hide();
      this.hide();
    },
    handleDiscard() {
      if (this.hasDataChanged) {
        this.$refs.discardEditingModal.show();
      } else {
        this.hide();
      }
    },
    getVacationDetail(vacationId) {
      this.isLoadingVacation = true;
      return this.getEventById({ eventId: vacationId }).then(() => {
        this.startDate = moment(this.currentEvent.startDateTime).utc().format('YYYY-MM-DD');
        this.endDate = moment(this.currentEvent.endDateTime).utc().format('YYYY-MM-DD');
        this.comment = this.currentEvent.description?.html ?? '';
        this.isLoadingVacation = false;
      });
    },
    getReportedChildren() {
      let children = [];
      if (
        this.getPresenceActiveModules[presenceModules.VACATION] != null &&
        this.getPresenceActiveModules[presenceModules.VACATION].length > 0
      ) {
        children = this.children
          .filter(
            child =>
              this.getPresenceActiveModules[presenceModules.VACATION].includes(child.id) &&
              this.hasCustodyOrExtendedAccess(child.id)
          )
          .map(child => ({
            ...child,
            isSelected: this.isCreating || (this.currentEvent && child.id === this.currentEvent.creator.id),
          }));
      }
      this.reportedChildren = children;
    },
    validateChildren() {
      this.hasDataChanged = true;
      const selectedChildIds = this.reportedChildren.filter(child => child.isSelected).map(child => child.id);
      this.isMissingChildren = selectedChildIds.length === 0;
    },
    onSaveClicked() {
      this.isMissingStartDate = false;
      this.isMissingEndDate = false;

      if (!this.startDate) {
        this.isMissingStartDate = true;
      }
      if (!this.endDate) {
        this.isMissingEndDate = true;
      }

      const isDateInvalid = !this.isEndDateValid();
      const isValid = !this.isMissingChildren && !this.isMissingStartDate && !this.isMissingEndDate && !isDateInvalid;

      if (this.isReadonly) {
        this.hide();
      } else if (isValid) {
        if (this.isCreating) {
          this.createVacation();
        } else {
          this.updateVacation();
        }
      }
    },
    createVacation() {
      this.isSavingVacation = true;
      const selectedChildIds = this.reportedChildren.filter(child => child.isSelected).map(child => child.id);
      if (selectedChildIds.length > 0) {
        const params = {
          childIds: selectedChildIds,
          intervals: [
            {
              startDate: this.startDate,
              endDate: this.endDate,
            },
          ],
          comment: this.comment,
        };
        this.addAbsence(params)
          .then(() => {
            this.hide();
          })
          .finally(() => {
            this.isSavingVacation = false;
          });
      }
    },
    updateVacation() {
      this.isSavingVacation = true;
      const params = {
        eventId: this.currentEvent.id,
        startDateTime: this.startDate,
        endDateTime: this.endDate,
        type: eventTypeEnum.PRESENCE_HOLIDAY,
        description: this.comment,
        title: this.currentEvent.title,
        allDay: true,
        private: false,
        responseRequired: this.currentEvent.responseRequired,
        addedToInstitutionCalendar: this.currentEvent.addedToInstitutionCalendar,
        hideInOwnCalendar: this.currentEvent.hideInOwnCalendar,
        isEditEvent: true,
      };
      this.updateSimpleEvent(params)
        .then(() => {
          this.hide();
        })
        .finally(() => {
          this.isSavingVacation = false;
        });
    },
    deleteVacation() {
      this.isDeletingVacation = true;
      const params = {
        eventForm: this.currentEvent,
        type: eventTypeEnum.PRESENCE_HOLIDAY,
      };
      this.deleteEvent(params)
        .then(() => {
          this.$refs.deletionWarningModal.hide();
          this.hide();
        })
        .finally(() => {
          this.isDeletingVacation = false;
        });
    },
  },
};
</script>
<style lang="scss" scoped>
@use '../../../shared/assets/scss/core/variables' as *;
@use '../../../shared/assets/scss/core/breakpoints' as *;
@use '../../../shared/assets/scss/components/presence/_presence' as *;
</style>
